/**
 * FilterDropdown Component
 *
 * A reusable dropdown component designed for filtering data across the application.
 * This component serves as the foundation for all filter dropdowns in the application,
 * providing consistent UI and behavior for filtering in different contexts.
 *
 * Features:
 * - Support for flat list of options or sectioned options
 * - Single or multiple selection modes
 * - Clear selection functionality
 * - Mobile-friendly design
 * - Customizable appearance with className props
 * - Support for custom trigger elements
 *
 * Used by specialized filter components:
 * - Dashboard filters: DashboardTagFilterDropdown, DashboardUserFilterDropdown
 * - Task filters: TaskStatusFilterDropdown, TaskTagFilterDropdown, TaskAssigneeFilterDropdown
 *
 * @see /src/components/ui/README-FILTERS.md for detailed documentation
 */
import React, { useState } from 'react';
import { Check, Filter } from 'lucide-react';
import { cn } from '../../lib/utils';
import { FilterButton } from './FilterButton';
import { MobileFullScreenModal, ModalDoneFooter } from './MobileFullScreenModal';

// No longer need simple duplicate components as we now use DropdownContent and DropdownDivider

/**
 * Represents a single option in a filter dropdown
 */
interface FilterOption<T extends string = string> {
  id: T; // Unique identifier for the option
  label: string; // Display text for the option
  icon?: React.ReactNode; // Optional icon to display with the option
  avatar?: React.ReactNode; // For user avatars or other visual representations
  section?: string; // Optional section name for grouping
}

/**
 * Represents a group of related filter options with a title
 */
interface FilterSection<T extends string = string> {
  title: string; // Section header text
  options: FilterOption<T>[]; // Options within this section
}

/**
 * Props for the FilterDropdown component
 */
interface FilterDropdownProps<T extends string = string> {
  // Data sources - can provide either options or sections
  options?: FilterOption<T>[]; // Flat list of filter options
  sections?: FilterSection<T>[]; // Grouped sections of filter options

  // Selection state and callbacks
  selectedValues: T[]; // Currently selected option IDs
  onChange: (selectedValues: T[]) => void; // Called when selection changes
  onClear?: () => void; // Optional callback to clear selections

  // Behavior configuration
  allowMultiple?: boolean; // Whether multiple options can be selected
  isMobile?: boolean; // Whether to use mobile-optimized UI
  mobilePosition?: number; // Position factor for mobile dropdown
  useFullScreenMobile?: boolean; // Whether to use full-screen modal on mobile

  // UI configuration
  label?: string; // Label used when multiple items selected
  placeholder?: string; // Text shown when nothing selected
  headerText?: string; // Header text for the dropdown
  modalTitle?: string; // Title for the full-screen mobile modal (defaults to headerText)
  className?: string; // CSS class for dropdown content
  buttonClassName?: string; // CSS class for trigger button
  trigger?: React.ReactNode; // Custom trigger element

  // For controlled open state
  open?: boolean; // Whether dropdown is open (controlled)
  onOpenChange?: (isOpen: boolean) => void; // Called when open state changes
}

export function FilterDropdown<T extends string = string>({
  options,
  sections,
  selectedValues,
  onChange,
  onClear,
  allowMultiple = false,
  label = '',
  placeholder = 'Filter',
  headerText = 'Select Options',
  modalTitle,
  isMobile = false,
  useFullScreenMobile = false,
  className = '',
  buttonClassName = '',
  open,
  onOpenChange,
  trigger,
}: FilterDropdownProps<T>) {
  // Internal state for uncontrolled component
  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const isOpen = open !== undefined ? open : internalOpen;

  // Function to handle open/close
  const handleOpenChange = (newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  // Add click outside handler and position calculation
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const dropdownContentRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<'left' | 'right'>('left');

  // Function to calculate optimal dropdown position
  const calculatePosition = React.useCallback(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const screenWidth = window.innerWidth;

      // Check if showing dropdown to the right would go offscreen
      // Specifically target user/people filters which are typically on the right side
      if (rect.right + 20 > screenWidth || rect.right > screenWidth * 0.8) {
        setPosition('right');
      } else {
        setPosition('left');
      }
    }
  }, [isOpen, dropdownRef.current]);

  // Calculate position when dropdown opens
  React.useEffect(() => {
    calculatePosition();

    // Also calculate position on resize to handle window changes
    if (isOpen) {
      window.addEventListener('resize', calculatePosition);
      return () => window.removeEventListener('resize', calculatePosition);
    }
  }, [isOpen, calculatePosition]);

  // Handle clicks outside the dropdown
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Don't handle outside clicks on mobile - the MobileFullScreenModal has its own close button
      if (isMobile) {
        return;
      }
      
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        handleOpenChange(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isMobile]);

  // Handle selecting an option
  const handleSelect = (optionId: T, isExclusive = !allowMultiple) => {
    if (isExclusive) {
      // If exclusive, replace all selections with this one
      if (selectedValues.includes(optionId)) {
        onChange([]);
      } else {
        onChange([optionId]);
      }
    } else {
      // Toggle the selected state of this option
      const newSelectedValues = selectedValues.includes(optionId)
        ? selectedValues.filter(id => id !== optionId)
        : [...selectedValues, optionId];

      onChange(newSelectedValues);
    }
  };

  // Handle clearing all filters
  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (onClear) {
      onClear();
    } else {
      onChange([]);
    }

    handleOpenChange(false);
  };

  // Get a flat list of all options (from sections or direct options)
  const getAllOptions = (): FilterOption<T>[] => {
    if (options) return options;
    if (sections) return sections.flatMap(section => section.options);
    return [];
  };

  // Get display label based on selection
  const getButtonLabel = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    }

    if (selectedValues.length === 1) {
      const selectedOption = getAllOptions().find(opt => opt.id === selectedValues[0]);
      return selectedOption?.label || placeholder;
    }

    return `${label || placeholder} (${selectedValues.length})`;
  };

  // Filter button with current selection
  // If a custom trigger is provided, use that instead
  const filterButton = trigger || (
    <FilterButton
      isActive={isOpen}
      hasFilters={selectedValues.length > 0}
      onClick={() => {}}
      onClear={handleClearAll}
      label={getButtonLabel()}
      icon={<Filter size={16} />}
      isMobile={isMobile}
      className={buttonClassName}
      boldWhenFiltered={true}
    />
  );

  // Render a single option item
  const renderOptionItem = (option: FilterOption<T>) => (
    <div
      key={option.id}
      className={cn(
        'w-full flex items-center text-gray-700 hover:bg-[var(--content-bg-color)] cursor-pointer',
        'px-4 py-3' // Consistent padding for all options
      )}
      onClick={() => handleSelect(option.id)}
    >
      <div className="flex-1 flex items-center gap-2 min-w-0">
        {option.avatar && option.avatar}
        {!option.avatar && option.icon && option.icon}
        <span className="pr-5 truncate">{option.label}</span>
      </div>

      <div
        className={cn(
          'w-5 h-5 flex items-center justify-center rounded-full border flex-shrink-0',
          selectedValues.includes(option.id) ? 'bg-green-500 border-green-500' : 'border-gray-300'
        )}
      >
        {selectedValues.includes(option.id) && <Check size={14} className="text-white" />}
      </div>
    </div>
  );

  // Render the dropdown content
  const renderDropdownContent = () => {
    return (
      <div className="bg-white overflow-hidden rounded-lg border-0 animate-dropdown-fade">
        <div className="overflow-hidden">
          {/* If we have sections, render them with dividers */}
          {sections ? (
            sections.map(section => (
              <React.Fragment key={section.title}>
                {/* Section header with standard styling */}
                <div className="px-3 py-2.5 bg-gray-50 flex items-center gap-1.5">
                  <h3 className="font-medium text-gray-700">{section.title}</h3>
                </div>

                {/* Section options */}
                {section.options.map(option => renderOptionItem(option))}
              </React.Fragment>
            ))
          ) : (
            /* If we just have a flat list of options, show the header */
            <>
              {/* Visible header for all dropdowns - using the component's headerText */}
              <div className="px-3 py-2.5 bg-gray-50 flex items-center gap-1.5">
                <h3 className="font-medium text-gray-700">{headerText}</h3>
              </div>
              {options?.map(option => renderOptionItem(option))}
            </>
          )}
        </div>
      </div>
    );
  };

  // If using full-screen mobile mode and dropdown is open
  if (isMobile && useFullScreenMobile && isOpen) {
    return (
      <div className="relative inline-block">
        {/* Trigger button */}
        <div onClick={() => handleOpenChange(!isOpen)}>{filterButton}</div>

        {/* Full screen mobile modal */}
        <MobileFullScreenModal
          isOpen={isOpen}
          onClose={() => handleOpenChange(false)}
          title={modalTitle || headerText}
          footerContent={<ModalDoneFooter onClose={() => handleOpenChange(false)} />}
        >
          {renderDropdownContent()}
        </MobileFullScreenModal>
      </div>
    );
  }

  // Standard dropdown for desktop or non-fullscreen mobile
  return (
    <div ref={dropdownRef} className="relative inline-block">
      {/* Trigger button */}
      <div onClick={() => handleOpenChange(!isOpen)}>{filterButton}</div>

      {/* Dropdown content */}
      {isOpen && (
        <div
          ref={dropdownContentRef}
          className={cn(
            'absolute z-50 mt-2 min-w-[300px] rounded-lg shadow-xl',
            // Use the calculated position based on screen position
            position === 'right' ? 'right-0' : 'left-0',
            className
          )}
        >
          {renderDropdownContent()}
        </div>
      )}
    </div>
  );
}
